import { Redirect, Route } from 'react-router-dom'
import { CIMSModule, pathify, resolveValueOrArray } from 'shared'
import { useAuthContext } from 'context/AuthContext'
import { useGlobalState } from 'context/GlobalStateContext'
import { notificationHandler } from 'hooks/notificationHandler'
import { ProtectedRouteProps } from './types'

/**
 * designed with inspiration from [here](https://v5.reactrouter.com/web/example/auth-workflow)
 *
 * @see Login component for more information
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  permissions,
  developer,
  children,
  ...rest
}) => {
  const {
    state: { projectGlobalId: projectId },
  } = useGlobalState()
  const { user } = useAuthContext()
  return (
    <Route
      {...rest}
      render={({ location: { pathname: from } }) => {
        if (!user) return <Redirect to={{ pathname: '/', state: { from } }} />

        const to = resolveValueOrArray(permissions ?? [])

        /**
         * if you don't set developer or permissions it's open
         */
        let authorized = true
        if (permissions)
          authorized = user.someAuthorized(to, projectId).authorized
        else if (developer) authorized = user.developer === true

        if (authorized) return children

        notificationHandler({
          title:
            'You do not have permission to access this resource. Please reach out to the primary CIMS contact if you feel this is incorrect.',
          alternateType: 'warning',
          details:
            process.env.NODE_ENV === 'production' ? undefined : (
              <pre style={{ maxHeight: 500, overflow: 'auto' }}>
                {JSON.stringify(
                  {
                    from,
                    to: rest.path,
                    routePermissions: developer
                      ? 'developer'
                      : to.map(({ name, id }) => ({ id, name })),
                    user: projectId
                      ? {
                          ...user.properties,
                          projects: undefined,
                          project: user.projects.find(
                            ({ id }) => id === projectId
                          ),
                        }
                      : { ...user.properties, projects: undefined },
                  },
                  null,
                  2
                )}
              </pre>
            ),
        })

        const { pathname } = CIMSModule.Home
        if (projectId) return <Redirect to={pathify(projectId, pathname)} />
        return <Redirect to={pathify(pathname)} />
      }}
    />
  )
}
